import { useState } from 'react'
import type { ServerApiClient } from '../../../../../data/ServerApiClient'
import type { CeoCockpitResponse, CeoCriticalInputs } from '../ceoCockpitTypes'

export type CeoSetupPanelProps = {
  payload: CeoCockpitResponse
  appApi: ServerApiClient
  workbookId: string | null
  /** Demo sessions may read the setup but never write it. */
  readOnly?: boolean
  onSaved: () => void
}

/** The outcomes a company can be judged on. Governed metrics only, so the
 *  target is always tied to the books rather than a free-text aspiration. */
const OUTCOMES: Array<{ id: string; label: string }> = [
  { id: 'sales', label: 'Sales' },
  { id: 'gross_profit', label: 'Gross profit' },
  { id: 'net_income', label: 'Profit' },
  { id: 'cash', label: 'Cash' },
]

/**
 * Where a company says what it is trying to move, and with what.
 *
 * Three fields, not thirty. The page's whole argument is that attention is
 * finite, and a setup screen that asks for everything is the same failure in a
 * different costume — a company that names eight critical inputs has named
 * none.
 */
export function CeoSetupPanel({
  payload,
  appApi,
  workbookId,
  readOnly,
  onSaved,
}: CeoSetupPanelProps) {
  const current: CeoCriticalInputs | null = payload.critical_inputs ?? null
  const plan = payload.derived_plan ?? null
  // Every field opens pre-filled with what the books already imply. An empty
  // box asks a reader to invent a number; a filled one asks them to disagree
  // with a real one, which is a question they can actually answer.
  const [outcome, setOutcome] = useState(current?.outcome_metric ?? 'sales')
  const [outcomeTarget, setOutcomeTarget] = useState(
    current?.outcome_target != null
      ? String(current.outcome_target)
      : plan?.outcome_target.value != null
        ? String(Math.round(plan.outcome_target.value))
        : '',
  )
  const [constraint, setConstraint] = useState(current?.constraint_note ?? '')
  const [materiality, setMateriality] = useState(
    current?.materiality_usd != null
      ? String(current.materiality_usd)
      : plan?.materiality_usd.value != null
        ? String(Math.round(plan.materiality_usd.value))
        : '',
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const save = async () => {
    if (!workbookId || readOnly) return
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      await appApi.putQuickBooksCeoCriticalInputs(workbookId, {
        outcome_metric: outcome || null,
        outcome_target: outcomeTarget.trim() ? Number(outcomeTarget) : null,
        constraint_note: constraint,
        materiality_usd: materiality.trim() ? Number(materiality) : null,
      })
      setSaved(true)
      onSaved()
    } catch (err) {
      // Say what failed. "Something went wrong" on a settings screen leaves a
      // reader unsure whether their change was recorded, which is worse than
      // the failure itself.
      setError(String((err as Error)?.message || err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="ceo-setup" data-testid="ceo-setup">
      <div className="ceo-strip-head">
        <span className="ceo-cap">Setup</span>
        <span className="ceo-hint">what this company is trying to move, and with what</span>
      </div>

      <div className="ceo-setup-card">
        <p className="ceo-setup-intro">
          {payload.current_source
            ? 'These are saved, user-entered operating targets and constraints. They do not qualify daily facts or create a forecast. Save explicitly to change them.'
            : 'Everything below is already working. These are the numbers your books imply, and the page has been using them since you connected QuickBooks — change any of them to be held to something else. Nothing here is a blocker.'}
        </p>

        <label className="ceo-field">
          <span className="ceo-field-label">The outcome you are judged on</span>
          <select
            value={outcome}
            disabled={readOnly}
            onChange={(event) => setOutcome(event.target.value)}
          >
            <option value="">Not set</option>
            {OUTCOMES.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="ceo-field-hint">
            Governed metrics only, so the target is always tied to your books.
          </span>
        </label>

        <label className="ceo-field">
          <span className="ceo-field-label">Its target for the month</span>
          <input
            type="number"
            inputMode="decimal"
            value={outcomeTarget}
            disabled={readOnly}
            placeholder="No target set"
            onChange={(event) => setOutcomeTarget(event.target.value)}
          />
          <span className="ceo-field-hint">
            {plan?.outcome_target.origin === 'derived'
              ? plan.outcome_target.basis
              : plan?.outcome_target.origin === 'unavailable'
                ? plan.outcome_target.basis
                : 'You set this target.'}
          </span>
        </label>

        <label className="ceo-field">
          <span className="ceo-field-label">What is limiting you right now</span>
          <textarea
            rows={2}
            value={constraint}
            disabled={readOnly}
            placeholder="We cannot hire technicians fast enough"
            onChange={(event) => setConstraint(event.target.value)}
          />
          <span className="ceo-field-hint">
            In your own words. A constraint is rarely a metric, and the truest sentence on most
            companies’ pages is not one.
          </span>
        </label>

        <label className="ceo-field">
          <span className="ceo-field-label">Smallest problem worth your morning</span>
          <input
            type="number"
            inputMode="decimal"
            value={materiality}
            disabled={readOnly}
            placeholder="5000"
            onChange={(event) => setMateriality(event.target.value)}
          />
          <span className="ceo-field-hint">
            {plan?.materiality_usd.origin === 'derived'
              ? `${plan.materiality_usd.basis} It decides what reaches Today at all.`
              : 'In dollars. It decides what reaches Today at all.'}
          </span>
        </label>

        {readOnly ? (
          <p className="ceo-setup-note">
            This is a demo company, so setup is read-only here.
          </p>
        ) : (
          <div className="ceo-inc-acts">
            <button type="button" className="cfo-btn sm primary" disabled={saving} onClick={save}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            {saved ? <span className="ceo-setup-note">Saved. Today will rebuild with it.</span> : null}
            {error ? <span className="ceo-setup-error">{error}</span> : null}
          </div>
        )}
      </div>

      {current?.configured && current.input_label ? (
        <p className="ceo-setup-note">
          Current lever: <b>{current.input_label}</b>
          {current.input_days_recorded_last_7 === 0
            ? ' — nothing recorded in the past week.'
            : ` — recorded on ${current.input_days_recorded_last_7} of the last 7 days.`}
        </p>
      ) : null}
    </section>
  )
}
